import {$isDynamicNode} from 'components/DSYS/editor/nodes/DynamicNode';
import type {LexicalEditor, NodeKey} from 'lexical';
import {$getNodeByKey, $getRoot, $isElementNode, type LexicalNode} from 'lexical';
import {useCallback} from 'react';

export type MergePersistedMapKeyArgs = {
  rawNodeValue: unknown;
  mapKey: string;
  serializedState: string | undefined;
  sourceText: string | undefined;
};

export type PersistEditorStateMapKey = (
  mapKey: string,
  serialized: string | undefined,
  sourceText: string | undefined
) => void;

const visitDescendants = (node: LexicalNode, visitor: (node: LexicalNode) => void): void => {
  visitor(node);
  if (!$isElementNode(node)) {
    return;
  }
  node.getChildren().forEach((child) => visitDescendants(child, visitor));
};

export function fanOutDynamicNodeEditorStateMapKey<T = unknown>({
  editor,
  sourceNodeKey,
  kindId,
  mapKey,
  serializedState,
  sourceText,
  mergePersistedMapKey,
}: {
  editor: LexicalEditor;
  sourceNodeKey: NodeKey;
  kindId: string;
  mapKey: string;
  serializedState: string | undefined;
  sourceText: string | undefined;
  mergePersistedMapKey: (args: MergePersistedMapKeyArgs) => T | undefined;
}): void {
  editor.update(
    () => {
      visitDescendants($getRoot(), (node) => {
        if (!$isDynamicNode(node)) {
          return;
        }
        if (node.getKey() === sourceNodeKey || node.getKindId() !== kindId) {
          return;
        }

        const next = mergePersistedMapKey({
          rawNodeValue: node.getValue(),
          mapKey,
          serializedState,
          sourceText,
        });
        if (next !== undefined) {
          node.setValue(next);
        }
      });
    },
    {discrete: true}
  );
}

/**
 * Persists one key into a dynamic node's value by reading the latest node inside a Lexical `read`,
 * then calling `mergePersistedMapKey` and `setValue` when the result is not `undefined`.
 */
export function useDynamicNodeEditorStateMap<T = unknown>({
  editor,
  nodeKey,
  setValue,
  mergePersistedMapKey,
}: {
  editor: LexicalEditor;
  nodeKey: NodeKey;
  setValue: (next: T) => void;
  mergePersistedMapKey: (args: MergePersistedMapKeyArgs) => T | undefined;
}): {persistKey: PersistEditorStateMapKey} {
  const persistKey = useCallback<PersistEditorStateMapKey>(
    (mapKey, serialized, sourceText) => {
      let next: T | undefined;
      editor.getEditorState().read(() => {
        const node = $getNodeByKey(nodeKey);
        if (!$isDynamicNode(node)) {
          return;
        }
        next = mergePersistedMapKey({
          rawNodeValue: node.getValue(),
          mapKey,
          serializedState: serialized,
          sourceText,
        });
      });
      if (next !== undefined) {
        setValue(next);
      }
    },
    [editor, mergePersistedMapKey, nodeKey, setValue]
  );

  return {persistKey};
}
